import type { TextSpacingModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { TEXT_SPACING_OPTIONS } from "@v2/features/modifiers/constants";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { ChevronDown, LucideMoveHorizontal } from "lucide-react";
import { parseUnit } from "replo-runtime/shared/utils/units";

interface TextSpacingModifierProps {
  onChange: (modifiers: TextSpacingModifierValue[]) => void;
}

const TextSpacingModifier: React.FC<TextSpacingModifierProps> = ({
  onChange,
}) => {
  const currentValue =
    useOptimisticModifierValue({
      modifierType: { type: "textSpacing", value: 0 },
      extractor: (elementDesignation) => {
        return elementDesignation.element.styleOverrides?.letterSpacing ?? null;
      },
    }) ?? null;

  const displayValue = currentValue != null ? currentValue.toString() : null;

  const menuOptions = TEXT_SPACING_OPTIONS.map((option) => ({
    label: option.label,
    value: option.value.toString(),
  }));

  const handleChange = (newValue: string) => {
    const result = parseUnit(
      newValue,
      { value: 0, unit: "em" },
      "textSpacing",
      "em",
    );

    onChange([
      {
        type: "textSpacing" as const,
        value: Number(result.value) ?? 0,
      },
    ]);
  };

  return (
    <div className="text-spacing-modifier">
      <LengthInputSelector
        field="textSpacing"
        value={displayValue}
        startEnhancer={<LucideMoveHorizontal size={12} />}
        onChange={handleChange}
        placeholder="0em"
        metrics={["em"]}
        unitDefaults={{ value: "0", unit: "em" }}
        menuOptions={menuOptions}
        allowsNegativeValue
        /* NOTE (Kevin, 2025-12-11): a value of more than 0.5em
         or less than -0.5em looks very off, so we limit the range. */
        minValues={{ em: -0.5 }}
        maxValues={{ em: 0.5 }}
        inputClassName="w-full"
        endEnhancer={() => <ChevronDown size={12} />}
      />
    </div>
  );
};

export default TextSpacingModifier;
